import { Link, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Moon, Sun, Menu, X, Search, LogOut, User, Home, MapPin, Edit2, Shield } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/lib/supabaseClient";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import type { UserLocation } from "@/lib/simulator";
import leilizeLogo from "@/assets/leilize-logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const BRAZIL_STATES = [
  { uf: "AC", nome: "Acre" },
  { uf: "AL", nome: "Alagoas" },
  { uf: "AP", nome: "Amapá" },
  { uf: "AM", nome: "Amazonas" },
  { uf: "BA", nome: "Bahia" },
  { uf: "CE", nome: "Ceará" },
  { uf: "DF", nome: "Distrito Federal" },
  { uf: "ES", nome: "Espírito Santo" },
  { uf: "GO", nome: "Goiás" },
  { uf: "MA", nome: "Maranhão" },
  { uf: "MT", nome: "Mato Grosso" },
  { uf: "MS", nome: "Mato Grosso do Sul" },
  { uf: "MG", nome: "Minas Gerais" },
  { uf: "PA", nome: "Pará" },
  { uf: "PB", nome: "Paraíba" },
  { uf: "PR", nome: "Paraná" },
  { uf: "PE", nome: "Pernambuco" },
  { uf: "PI", nome: "Piauí" },
  { uf: "RJ", nome: "Rio de Janeiro" },
  { uf: "RN", nome: "Rio Grande do Norte" },
  { uf: "RS", nome: "Rio Grande do Sul" },
  { uf: "RO", nome: "Rondônia" },
  { uf: "RR", nome: "Roraima" },
  { uf: "SC", nome: "Santa Catarina" },
  { uf: "SP", nome: "São Paulo" },
  { uf: "SE", nome: "Sergipe" },
  { uf: "TO", nome: "Tocantins" },
];

// Alvos que podem ser "encontrados" pela barra de busca (sem criar página /busca)
const ROUTES = [
  { label: "Página Inicial", to: "/", keywords: ["home", "início", "inicio", "inicial", "autocar"] },
  { label: "Como Funciona", to: "/como-funciona", keywords: ["como funciona", "passos", "guia", "funciona"] },
  { label: "Preços", to: "/precos", keywords: ["preços", "precos", "planos", "valores"] },
  { label: "Demo", to: "/demo", keywords: ["demo", "demonstração", "demonstracao", "teste", "scan"] },
  {
    label: "Cadastro",
    to: "/cadastro",
    keywords: ["cadastro", "começar", "comecar", "assinar", "registrar", "registro", "sign up"],
  },
  { label: "Login", to: "/login", keywords: ["login", "entrar", "acessar", "signin"] },
];

function chooseRoute(query: string) {
  const s = query.trim().toLowerCase();
  if (!s) return ROUTES[0];

  let best = { score: -1, idx: 0 };

  ROUTES.forEach((r, i) => {
    const tokens = [r.label.toLowerCase(), ...r.keywords.map((k) => k.toLowerCase())];
    let score = 0;
    if (tokens.some((t) => t === s)) score += 6;
    if (tokens.some((t) => t.startsWith(s))) score += 4;
    if (tokens.some((t) => t.includes(s))) score += 2;
    if (score > best.score) best = { score, idx: i };
  });

  return ROUTES[best.idx];
}

function getInitials(name?: string | null, email?: string | null): string {
  if (name) {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return "U";
}

export function Header() {
  const { theme, toggleTheme, mounted } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Location state
  const [userLocation, setUserLocation] = useLocalStorage<UserLocation>("user_location", { cidade: "", estado: "" });
  const [locationInput, setLocationInput] = useState({ cidade: "", estado: "" });
  const [locationOpen, setLocationOpen] = useState(false);

  // Geocoding on mount
  useEffect(() => {
    if (userLocation.cidade) return;

    const fetchByIP = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const data = await res.json();
        if (data.city && data.region_code) {
          setUserLocation({ cidade: data.city, estado: data.region_code });
          setLocationInput({ cidade: data.city, estado: data.region_code });
        }
      } catch (e) {
        console.warn("IP Geocoding failed:", e);
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`,
              { headers: { "User-Agent": "AutoCarBid/1.0" } }
            );
            const geo = await res.json();
            const cidade = geo.address?.city || geo.address?.town || geo.address?.municipality || "";
            const estado = geo.address?.state_code || geo.address?.state || "";
            
            // Map state name to UF if needed
            let finalEstado = estado;
            if (estado.length > 2) {
              const found = BRAZIL_STATES.find(s => s.nome.toLowerCase() === estado.toLowerCase());
              if (found) finalEstado = found.uf;
            }

            if (cidade || finalEstado) {
              const loc = { cidade, estado: finalEstado };
              setUserLocation(loc);
              setLocationInput(loc);
            }
          } catch (e) {
            console.warn("Nominatim Geocoding failed, falling back to IP:", e);
            fetchByIP();
          }
        },
        () => {
          console.log("Geolocation denied, falling back to IP...");
          fetchByIP();
        },
        { timeout: 5000 }
      );
    } else {
      fetchByIP();
    }
  }, []);

  // Auth state listener
  useEffect(() => {
    async function checkAdmin(userId: string) {
      const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
      setIsAdmin(!!data);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) checkAdmin(session.user.id);
        else setIsAdmin(false);
      }
    );

    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
      setLoading(false);
      if (data.session?.user) checkAdmin(data.session.user.id);
    });

    return () => subscription.unsubscribe();
  }, []);

  async function handleLogout() {
    await supabase.auth.signOut();
    setMobileMenuOpen(false);
    navigate("/");
  }

  // Atalho Ctrl/Cmd + K abre a busca. Esc fecha.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isCmdK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k";
      if (isCmdK) {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") setSearchOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (searchOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      setTimeout(() => inputRef.current?.focus(), 10);
      return () => {
        document.body.style.overflow = prev;
      };
    } else {
      setQ("");
    }
  }, [searchOpen]);

  const suggestions = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return ROUTES;
    return ROUTES.filter(
      (r) => r.label.toLowerCase().includes(s) || r.keywords.some((k) => k.toLowerCase().includes(s)),
    );
  }, [q]);

  function submitSearch(e?: React.FormEvent) {
    e?.preventDefault();
    const target = chooseRoute(q);
    setSearchOpen(false);
    navigate(target.to);
  }

  const userName = user?.user_metadata?.full_name || user?.user_metadata?.name;
  const userEmail = user?.email;
  const userAvatar = user?.user_metadata?.avatar_url;

  const locationLabel = userLocation.cidade
    ? `${userLocation.cidade}${userLocation.estado ? `, ${userLocation.estado}` : ""}`
    : null;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-sm">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group" aria-label="Ir para a página inicial LEILIZE">
            <img
              src={leilizeLogo}
              alt="LEILIZE"
              className="h-[3.8rem] w-auto group-hover:scale-105 transition-transform"
            />
          </Link>

          {/* Menu desktop */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/como-funciona"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors focus-ring rounded-md px-3 py-2"
            >
              Como Funciona
            </Link>
            <Link
              to="/precos"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors focus-ring rounded-md px-3 py-2"
            >
              Preços
            </Link>
            <Link
              to="/demo"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors focus-ring rounded-md px-3 py-2"
            >
              Demo
            </Link>
            {user && (
              <Link
                to="/userhome"
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors focus-ring rounded-md px-3 py-2"
              >
                Minhas Simulações
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin"
                className="text-sm font-medium text-destructive hover:text-destructive/80 transition-colors focus-ring rounded-md px-3 py-2 flex items-center gap-1"
              >
                <Shield className="w-3.5 h-3.5" />
                Admin
              </Link>
            )}
          </div>

          {/* Ações */}
          <div className="flex items-center gap-2">
            {/* Location badge */}
            <Popover open={locationOpen} onOpenChange={setLocationOpen}>
              <PopoverTrigger asChild>
                <button
                  className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-full border border-border/50 hover:bg-muted transition-colors"
                  title="Informe sua região para análises locais"
                >
                  <MapPin className="w-3.5 h-3.5 text-primary" />
                  <span className="max-w-[120px] truncate">
                    {locationLabel || "Sua região"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 space-y-3">
                <p className="text-sm font-semibold">📍 Sua localização</p>
                <p className="text-xs text-muted-foreground">
                  Informe sua região para que a análise considere preços e peças da sua localidade.
                </p>
                <Input
                  placeholder="Cidade"
                  value={locationInput.cidade}
                  onChange={(e) => setLocationInput((p) => ({ ...p, cidade: e.target.value }))}
                  className="h-9 px-3 text-sm"
                />
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  value={locationInput.estado}
                  onChange={(e) => setLocationInput((p) => ({ ...p, estado: e.target.value }))}
                >
                  <option value="">Selecione o Estado</option>
                  {BRAZIL_STATES.map((s) => (
                    <option key={s.uf} value={s.uf}>
                      {s.uf} - {s.nome}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  className="w-full font-bold uppercase tracking-wider text-[10px]"
                  onClick={() => {
                    if (locationInput.cidade || locationInput.estado) {
                      setUserLocation(locationInput);
                    }
                    setLocationOpen(false);
                  }}
                >
                  Salvar Região
                </Button>
              </PopoverContent>
            </Popover>

            {/* Busca */}
            <button
              onClick={() => setSearchOpen(true)}
              className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted transition-colors focus-ring"
              aria-label="Abrir busca (Ctrl/⌘+K)"
              title="Buscar (Ctrl/⌘+K)"
            >
              <Search className="w-5 h-5 text-foreground" />
            </button>

            {/* Tema */}
            {mounted && (
              <button
                onClick={toggleTheme}
                className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted transition-colors focus-ring"
                aria-label="Alternar tema"
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5 text-foreground" />
                ) : (
                  <Moon className="w-5 h-5 text-foreground" />
                )}
              </button>
            )}

            {/* Auth buttons / User menu */}
            {!loading && (
              <>
                {user ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="hidden md:flex items-center gap-2 rounded-full hover:ring-2 hover:ring-primary/50 transition-all focus-ring">
                        <Avatar className="h-9 w-9 border-2 border-primary/20">
                          <AvatarImage src={userAvatar} alt={userName || userEmail || "Usuário"} />
                          <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                            {getInitials(userName, userEmail)}
                          </AvatarFallback>
                        </Avatar>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                          {userName && <p className="text-sm font-medium">{userName}</p>}
                          <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/userhome" className="cursor-pointer">
                          <Home className="mr-2 h-4 w-4" />
                          Minha Área
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive">
                        <LogOut className="mr-2 h-4 w-4" />
                        Sair
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <div className="hidden md:flex items-center gap-2">
                    <Button variant="ghost" asChild>
                      <Link to="/login">Entrar</Link>
                    </Button>
                    <Button asChild>
                      <Link to="/cadastro">Começar Agora</Link>
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Menu mobile */}
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="md:hidden w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted transition-colors focus-ring"
              aria-label="Menu"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-foreground" />
              ) : (
                <Menu className="w-5 h-5 text-foreground" />
              )}
            </button>
          </div>
        </div>

        {/* Drawer mobile */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden"
            >
              <div className="py-4 space-y-3 border-t border-border/50">
                {/* Mobile location */}
                <button
                  onClick={() => { setLocationOpen(true); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted"
                >
                  <MapPin className="w-4 h-4 text-primary" />
                  {locationLabel || "Informe sua região"}
                </button>

                <Link
                  to="/como-funciona"
                  className="block px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Como Funciona
                </Link>
                <Link
                  to="/precos"
                  className="block px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Preços
                </Link>
                <Link
                  to="/demo"
                  className="block px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Demo
                </Link>
                {user && (
                  <Link
                    to="/userhome"
                    className="block px-3 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors rounded-lg hover:bg-muted"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Minhas Simulações
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-destructive hover:text-destructive/80 transition-colors rounded-lg hover:bg-muted"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Shield className="w-4 h-4" />
                    Admin
                  </Link>
                )}
                
                {!loading && (
                  <>
                    {user ? (
                      <>
                        <div className="px-3 py-2 border-t border-border/50 mt-2 pt-3">
                          <div className="flex items-center gap-3 mb-3">
                            <Avatar className="h-10 w-10 border-2 border-primary/20">
                              <AvatarImage src={userAvatar} alt={userName || userEmail || "Usuário"} />
                              <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                                {getInitials(userName, userEmail)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              {userName && <p className="text-sm font-medium truncate">{userName}</p>}
                              <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                            </div>
                          </div>
                          <Link
                            to="/userhome"
                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Home className="w-4 h-4" />
                            Minha Área
                          </Link>
                          <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors rounded-lg mt-1"
                          >
                            <LogOut className="w-4 h-4" />
                            Sair
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2 pt-2 border-t border-border/50">
                        <Button variant="outline" asChild className="w-full">
                          <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                            Entrar
                          </Link>
                        </Button>
                        <Button asChild className="w-full">
                          <Link to="/cadastro" onClick={() => setMobileMenuOpen(false)}>
                            Começar Agora
                          </Link>
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Overlay de busca */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSearchOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="mx-auto mt-24 w-full max-w-xl px-4"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Busca"
            >
              <form onSubmit={submitSearch} className="rounded-xl border bg-background shadow-lg overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3">
                  <Search className="w-5 h-5 text-muted-foreground" />
                  <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar páginas (Ctrl/⌘+K)"
                    className="flex-1 bg-transparent outline-none text-sm md:text-base"
                  />
                  <kbd className="hidden md:inline-flex text-xs text-muted-foreground border rounded px-1.5 py-0.5">
                    Enter
                  </kbd>
                </div>

                <div className="border-t divide-y">
                  {suggestions.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setSearchOpen(false)}
                      className="block px-4 py-2.5 text-sm hover:bg-muted"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>

                <div className="flex items-center justify-between px-4 py-2 bg-muted/40 text-xs text-muted-foreground">
                  <span>Dica: digite e pressione Enter para ir ao melhor resultado.</span>
                  <span>Esc para fechar</span>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
